export interface IBaseDictionary {
  id: number;
  name: string;
}

export interface IDictionaryWithOrder extends IBaseDictionary {
  order: number;
}

export interface ICountryModel extends IDictionaryWithOrder {}
export interface ILanguagesModel extends IDictionaryWithOrder {}
export interface IBusinessUnitsModel extends IDictionaryWithOrder {}
export interface ILeadTagsModel extends IDictionaryWithOrder {
  colorAlias: string;
}
export interface IDeviceTypeModel extends IDictionaryWithOrder {}
export interface IAffiliateSystemNameModel extends IDictionaryWithOrder {}
export interface IEligibilityRejectReasonModel extends IDictionaryWithOrder {}
export interface ISalesStatusRejectReasonModel extends IDictionaryWithOrder {}
export interface ISalesStatusModel extends IDictionaryWithOrder {}
export interface IComplianceStatusModel extends IDictionaryWithOrder {}
export interface ITradingStatusModel extends IDictionaryWithOrder {}
export interface IEmailTemplateTypeModel extends IDictionaryWithOrder {}
export interface IHistoryTypeModel extends IDictionaryWithOrder {}

export interface IDocumentsRejectedReasons extends IDictionaryWithOrder {}

export interface IBusinessUnitModel extends IBusinessUnitsModel {
  parentId?: number;
  environmentId?: number;
}

export interface IBusinessUnitByEnvironmentModel
  extends Exclude<IBusinessUnitModel, 'parentId'> {
  isDemo: boolean;
}

export interface IQuotesAssetsModel extends IBaseDictionary {
  currency: string;
}

export interface IDividendStocksModel extends IBaseDictionary {
  stockId: number;
  symbol: string;
  companyName: string;
  exchangeName: string;
  webLink: string;
  flag: string;
}

export interface IManagerSignaturesByEnvironmentModel extends IBaseDictionary {
  environmentId: number;
  languageId: number;
  signature: string;
}

export interface IEnvironmentModel extends IBaseDictionary {
  guid?: string;
  inheritance: string;
  userDomainId?: number;
  friendlyName?: string;
}

export interface ITimeZoneModel extends IBaseDictionary {
  ianaName: string;
  offset: string;
}

interface IStateModel extends IBaseDictionary {
  stateCode: string;
}

export interface ICountryStateModel {
  countryId: number;
  states: IStateModel[];
}

export interface IAvailableBrandModel {
  environmentId: number;
  domainName: string;
}

export interface ILanguageModel extends IDictionaryWithOrder {
  code?: string;
}

export interface ICallUrlTemplatesModel extends IBaseDictionary {
  callCenterType: number;
  isInactive: boolean;
  template: string;
}

export interface ISmsShortGroupsModel extends IBaseDictionary {
  domainId: number;
  contactCount: number;
}

export interface ISmsCenterAssetModel extends IBaseDictionary {
  ask: number;
  bid: number;
}

export interface IPaymentGroupModel extends IDictionaryWithOrder {}

export interface IVideoVerificationStatusModel extends IDictionaryWithOrder {
  alias: string;
}

export interface ISubChannelModel extends IDictionaryWithOrder {}

export interface ILeadTagModel extends ILeadTagsModel {
  colorAlias: LeadColorAlias;
  readOnly: boolean;
}

export interface ISmsProviderModel extends IBaseDictionary {
  environmentId: number;
  smsProviderType: string;
}

export interface IAddSubChannelModel {
  id: number;
  channelId: number;
  name: string;
}

export interface IChannelModel extends IBaseDictionary {
  channelTypeId: number;
  isActive: boolean;
  description?: string;
  tags?: string;
  subChannels: IAddSubChannelModel[];
}

export interface ICountriesModel extends ILanguagesModel {
  countryCode: string;
  countryIsoCode: string;
  countryIsoNumericCode: string;
  countryIddCode: string;
  phoneCodes: number[];
}

export interface ITransactionSubtypesModel extends IDictionaryWithOrder {
  transactionType: string;
  transactionTypeId: number;
}

export interface ICurrenciesModel {
  platformId: number;
  platformName: string;
  code: string;
  precision: number;
}

export interface ICallProviderModel extends IBaseDictionary {
  providerCode: string;
  targetName: string;
  targetId: string;
  callerId: string;
  appendPhone: string;
  replacements: IReplacement[];
  templateId: number;
  countryIds: number[];
  isInactive: boolean;
}

export interface ICallCenterTypeModel extends IBaseDictionary {
  type: string;
}

export interface ISmsProviderTypeModel extends IBaseDictionary {
  type: SmsProviderTypes;
}

export interface ITradingPlatformsModel extends IDictionaryWithOrder {
  tradingPlatformTypeId: number;
  tradingPlatformTypeName: string;
  url: string;
}

export interface IWithdrawRequestStatusModel extends IBaseDictionary {
  alias: string;
  order: number;
}

export interface ITeamsDepartmentModel extends IBaseDictionary {}

export interface ITeamsLocationModel extends IDictionaryWithOrder {
  timeZone: string;
}

export interface IProcessorsModel extends IBaseDictionary {
  isAutomatic: boolean;
  processors: Array<string>;
}

interface IReplacement {
  from: string;
  to: string;
}

export enum SmsProviderTypes {
  SMS_CENTER = 'SmsCenter',
  JUST_CALL = 'JustCall',
  SANCOM = 'Sancom',
  BULK_SMS = 'BulkSms',
  BULK_GATE = 'BulkGate',
}

export enum LeadColorAlias {
  GRAY = 'gray',
  BROWN = 'brown',
  BLACK = 'black',
  YELLOW = 'yellow',
  ORANGE = 'orange',
  MUSTARD = 'mustard',
  RED = 'red',
  PINK = 'pink',
  PURPLE = 'purple',
  GREEN = 'green',
  HERBAL = 'herbal',
  AQUAMARINE = 'aquamarine',
  BLUE = 'blue',
  ELECTRIC_BLUE = 'lightskyblue',
  AZURE = 'azure',
}

export interface ISmsUserDomainModel extends IBaseDictionary {}

export interface IMailListUserDomainModel extends IDictionaryWithOrder {}

export interface IAffiliateSystemsModel extends IBaseDictionary {}

export interface IUserDomainsModel extends IBaseDictionary {}

export interface IRightsModel extends IBaseDictionary {}

export interface ITrackingLinksModel extends IBaseDictionary {
  linkId: string;
}

export interface IManagerModel extends IBaseDictionary {
  disabled?: boolean;
  businessUnits: number[];
  /** Only for first element of list */
  orders?: number;
}

export interface IManagersInTeam extends IBaseDictionary {
  businessUnits: number[];
  isAssignable?: boolean;
}
export interface IManagersByTeam {
  name: string;
  managers?: IManagersInTeam[];
}

export interface ISalesManagersByTeam extends IManagersByTeam {}
export interface IRetentionManagersByTeam extends IManagersByTeam {}
export interface IVipManagersByTeam extends IManagersByTeam {}
export interface ICloserManagersByTeam extends IManagersByTeam {}

export interface IRoleModel extends IBaseDictionary {
  isSystem: boolean;
  rights: number[];
}

export interface ITransactionSubTypeModel extends IBaseDictionary {
  transactionType: string;
  transactionTypeId: number;
  order: number;
}

export interface IOtherDepositTransactionSubTypeModel extends IBaseDictionary {
  allowedToGeneratePaymentAddress: boolean;
  allowedToDeposit: boolean;
}

export interface ICardProcessingEnvironment {
  businessUnitId: number;
  cardProcessingEnvironment: string;
  cardProcessingEnvironmentParam: string;
  cardProcessingUrl: string;
}

export interface IDocumentTypeModel extends IBaseDictionary {
  category: string;
  sumSubstanceId: string;
  textName: string;
}

export interface IManagerSignatureModel extends IBaseDictionary {
  environmentId: number;
  languageId: number;
  signature: string;
}

export interface IOrganizationUnitTypeModel extends IBaseDictionary {}
export interface ISumAndSubstanceTypeModel extends IBaseDictionary {}

export interface IConversionAgentModel extends IManagerModel {}
export interface ICloserManagerModel extends IManagerModel {}
export interface IRetentionManagerModel extends IManagerModel {}
export interface IVipManagerModel extends IManagerModel {}
export interface ISalesManagerModel extends IManagerModel {}
export interface IFirstContactManagerModel extends IManagerModel {}
export interface IConversionAgentCreatedModel extends IManagerModel {}
export interface ICloserManagerCreatedModel extends IManagerModel {}
export interface IRetentionManagerCreatedModel extends IManagerModel {}
export interface IVipManagerCreatedModel extends IManagerModel {}

export interface ITradingSignalAssetsModel extends IBaseDictionary {}

export interface IDictionariesByType {
  country: ICountryModel[];
  languages: ILanguagesModel[];
  businessUnits: IBusinessUnitsModel[];
  leadTags: ILeadTagsModel[];
  deviceType: IDeviceTypeModel[];
  affiliateSystemName: IAffiliateSystemNameModel[];
  eligibilityRejectReason: IEligibilityRejectReasonModel[];
  salesStatusRejectReason: ISalesStatusRejectReasonModel[];
  salesStatus: ISalesStatusModel[];
  complianceStatus: IComplianceStatusModel[];
  tradingStatus: ITradingStatusModel[];
  emailTemplateType: IEmailTemplateTypeModel[];
  historyType: IHistoryTypeModel[];
  transactionType: IDictionaryWithOrder[];
}

export interface ITransactionSubTypesDictionariesByType {
  pullbackTransactionSubTypes: ITransactionSubTypeModel[];
  withdrawRequestTransactionSubTypes: ITransactionSubTypeModel[];
  binaryOptionRolloverAddonPurchaseTransactionSubTypes: ITransactionSubTypeModel[];
  recallReversalTransactionSubTypes: ITransactionSubTypeModel[];
  recallTransactionSubTypes: ITransactionSubTypeModel[];
  interestTransactionSubTypes: ITransactionSubTypeModel[];
  executionTransactionSubTypes: ITransactionSubTypeModel[];
  chargebackReversalTransactionSubTypes: ITransactionSubTypeModel[];
  chargebackTransactionSubTypes: ITransactionSubTypeModel[];
  binaryOptionProfitTransactionSubTypes: ITransactionSubTypeModel[];
  binaryOptionPurchaseTransactionSubTypes: ITransactionSubTypeModel[];
  swapTransactionSubTypes: ITransactionSubTypeModel[];
  balanceTransferInTransactionSubTypes: ITransactionSubTypeModel[];
  balanceTransferOutTransactionSubTypes: ITransactionSubTypeModel[];
  depositCommissionTransactionSubTypes: ITransactionSubTypeModel[];
  rolloverCommissionTransactionSubTypes: ITransactionSubTypeModel[];
  tradingCommissionTransactionSubTypes: ITransactionSubTypeModel[];
  withdrawalCommissionTransactionSubTypes: ITransactionSubTypeModel[];
  withdrawalTransactionSubTypes: ITransactionSubTypeModel[];
  balanceCorrectionTransactionSubTypes: ITransactionSubTypeModel[];
  debitTransactionSubTypes: ITransactionSubTypeModel[];
  creditTransactionSubTypes: ITransactionSubTypeModel[];
  balanceTransactionTypes: IBaseDictionary[];
  depositTransactionSubTypes: ITransactionSubTypeModel[];
  profitLossTransactionSubTypes: ITransactionSubTypeModel[];
  internalTransferTransactionSubTypes: ITransactionSubTypeModel[];
}

export interface IDictionariesWithApi {
  environment: IEnvironmentModel[];
  callUrlTemplates: ICallUrlTemplatesModel[];
  businessUnit: IBusinessUnitModel[];
  language: ILanguageModel[];
  businessUnitByEnvironment: IBusinessUnitByEnvironmentModel[];
  managerSignaturesByEnvironment: IManagerSignaturesByEnvironmentModel[];
  smsShortGroups: ISmsShortGroupsModel[];
  smsUserDomain: ISmsUserDomainModel[];
  mailListUserDomain: IMailListUserDomainModel[];
  smsCenterAsset: ISmsCenterAssetModel[];
  timeZone: ITimeZoneModel[];
  tradingStatus: ITradingStatusModel[];
  leadTag: ILeadTagModel[];
  closerManager: ICloserManagerModel[];
  vipManager: IVipManagerModel[];
  salesManager: ISalesManagerModel[];
  conversionAgent: ISalesManagerModel[];
  retentionManager: IRetentionManagerModel[];
  countryStates: ICountryStateModel[];
  callProviders: ICallProviderModel[];
  smsProviders: ISmsProviderModel[];
  availableBrands: IAvailableBrandModel[];
  countries: ICountriesModel[];
  smsProviderTypes: ISmsProviderTypeModel[];
  paymentGroups: IPaymentGroupModel[];
  channels: IChannelModel[];
  affiliateSystems: IAffiliateSystemsModel[];
  trackingLinks: ITrackingLinksModel[];
  videoVerificationStatuses: IVideoVerificationStatusModel[];
  callCenterTypes: ICallCenterTypeModel[];
  userDomains: IUserDomainsModel[];
  quotesAssets: IQuotesAssetsModel[];
  dividendStocks: IDividendStocksModel[];
  tradingPlatforms: ITradingPlatformsModel[];
  availableTradingPlatforms: ITradingPlatformsModel[];
  roles: IRoleModel[];
  otherDepositTransactionSubTypes: IOtherDepositTransactionSubTypeModel[];
  cardProcessingEnvironments: ICardProcessingEnvironment[];
  documentTypes: IDocumentTypeModel[];
  teamLocations: ITeamsLocationModel[];
  managerSignatures: IManagerSignatureModel[];
  organizationUnitTypes: IOrganizationUnitTypeModel[];
  sumAndSubstanceTypes: ISumAndSubstanceTypeModel[];
  transactionSubtypes: ITransactionSubtypesModel[];
  supportedTransactionTypes: IDictionaryWithOrder[];
  balanceTransactionTypes: IBaseDictionary[];
  leadSmsProviders: ISmsProviderModel[];
  currencies: ICurrenciesModel[];
  documentsRejectedReasons: IDocumentsRejectedReasons[];
}

export interface IDictionaries
  extends IDictionariesWithApi,
    IDictionariesByType,
    ITransactionSubTypesDictionariesByType {
  vipManagerCreated: IVipManagerCreatedModel[];
  retentionManagerCreated: IRetentionManagerCreatedModel[];
  conversionAgentCreated: IConversionAgentCreatedModel[];
  closerManagerCreated: ICloserManagerCreatedModel[];
  conversionAgent: IConversionAgentModel[];
  firstContactManager: IFirstContactManagerModel[];
  tradingSignalAssets: ITradingSignalAssetsModel[];
  subChannel: ISubChannelModel[];
  withdrawRequestStatus: IWithdrawRequestStatusModel[];
  processors: IProcessorsModel[];
  rights: IRightsModel[];
  location: ITeamsLocationModel[];
  department: ITeamsDepartmentModel[];
}

export const dictionariesByType: {
  [key in keyof IDictionariesByType]: string;
} = {
  country: 'Country',
  languages: 'Language',
  businessUnits: 'BusinessUnit',
  leadTags: 'LeadTags',
  deviceType: 'DeviceType',
  affiliateSystemName: 'AffiliateSystem',
  eligibilityRejectReason: 'EligibilityRejectReason',
  salesStatusRejectReason: 'SalesStatusRejectReason',
  salesStatus: 'SalesStatus',
  complianceStatus: 'ComplianceStatus',
  tradingStatus: 'TradingStatus',
  emailTemplateType: 'EmailTemplateType',
  historyType: 'HistoryType',
  transactionType: 'TransactionType',
};

export const TransactionTypes: Record<
  keyof ITransactionSubTypesDictionariesByType,
  string
> = {
  pullbackTransactionSubTypes: 'Pullback',
  withdrawRequestTransactionSubTypes: 'Withdraw Request',
  binaryOptionRolloverAddonPurchaseTransactionSubTypes:
    'Binary Option Rollover Addon Purchase',
  recallReversalTransactionSubTypes: 'Recall Reversal',
  recallTransactionSubTypes: 'Recall',
  interestTransactionSubTypes: 'Interest',
  executionTransactionSubTypes: 'Execution',
  chargebackReversalTransactionSubTypes: 'Chargeback Reversal',
  chargebackTransactionSubTypes: 'Chargeback',
  binaryOptionProfitTransactionSubTypes: 'Binary Option Profit',
  binaryOptionPurchaseTransactionSubTypes: 'Binary Option Purchase',
  swapTransactionSubTypes: 'Swap',
  balanceTransferInTransactionSubTypes: 'Balance Transfer In',
  balanceTransferOutTransactionSubTypes: 'Balance Transfer Out',
  depositCommissionTransactionSubTypes: 'Deposit Commission',
  rolloverCommissionTransactionSubTypes: 'Rollover Commission',
  tradingCommissionTransactionSubTypes: 'Trading Commission',
  withdrawalCommissionTransactionSubTypes: 'Withdrawal Commission',
  withdrawalTransactionSubTypes: 'Withdrawal',
  balanceCorrectionTransactionSubTypes: 'Balance Correction',
  debitTransactionSubTypes: 'Debit',
  creditTransactionSubTypes: 'Credit',
  depositTransactionSubTypes: 'Deposit',
  balanceTransactionTypes: 'Balance Types',
  profitLossTransactionSubTypes: 'ProfitLoss',
  internalTransferTransactionSubTypes: 'Internal Transfer',
};
